import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  Fingerprint, Smartphone, Shield, Key, Trash2, Plus,
  CheckCircle2, AlertCircle, Mail, RefreshCw, Lock
} from 'lucide-react';

// Helper to convert base64url to ArrayBuffer
const base64urlToBuffer = (base64url) => {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// Helper to convert ArrayBuffer to base64url
const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const SecuritySettings = () => {
  const { api, user } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [friendlyName, setFriendlyName] = useState('');
  const [preferredMethod, setPreferredMethod] = useState('email');
  const [webauthnSupported, setWebauthnSupported] = useState(false);

  // Check WebAuthn support
  useEffect(() => {
    const checkSupport = async () => {
      // Check if WebAuthn API is available in the browser
      if (window.PublicKeyCredential) {
        try {
          // First check: is the API available?
          setWebauthnSupported(true);
          
          // Additional check: is a platform authenticator available?
          // This is optional - we still allow registration even without platform auth
          // as users might have security keys or cross-platform authenticators
          const platformAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          console.log('Platform authenticator available:', platformAvailable);
        } catch (err) {
          console.log('WebAuthn check error:', err);
          // Even if platform check fails, WebAuthn might still work with security keys
          setWebauthnSupported(true);
        }
      } else {
        setWebauthnSupported(false);
      }
    };
    checkSupport();
  }, []);

  // Fetch credentials
  const fetchCredentials = useCallback(async () => {
    try {
      const res = await api.get('/webauthn/credentials');
      setCredentials(res.data.credentials || []);
      setPreferredMethod(user?.preferred_mfa_method || 'email');
    } catch (err) {
      console.error('Error fetching credentials:', err);
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  // Register new passkey
  const handleRegister = async () => {
    if (!webauthnSupported) {
      toast.error('Your browser does not support WebAuthn. Please use a modern browser.');
      return;
    }

    try {
      setRegistering(true);
      console.log('Starting passkey registration...');

      // Start registration
      const startRes = await api.post('/webauthn/register/start');
      const options = startRes.data.options;
      const challengeId = startRes.data.challenge_id;
      console.log('Registration options received:', options);

      // Convert options for navigator.credentials.create
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        rp: {
          id: options.rp.id,
          name: options.rp.name
        },
        user: {
          id: base64urlToBuffer(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName
        },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout || 60000,
        attestation: options.attestation || 'none',
        authenticatorSelection: {
          residentKey: options.authenticatorSelection?.residentKey || 'preferred',
          userVerification: options.authenticatorSelection?.userVerification || 'preferred'
        }
      };

      // Add excludeCredentials if present
      if (options.excludeCredentials && options.excludeCredentials.length > 0) {
        publicKeyOptions.excludeCredentials = options.excludeCredentials.map(cred => ({
          type: cred.type,
          id: base64urlToBuffer(cred.id),
          transports: cred.transports
        }));
      }

      console.log('Calling navigator.credentials.create...');
      
      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions
      });

      console.log('Credential created:', credential);

      // Convert credential for server
      const credentialJSON = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          attestationObject: bufferToBase64url(credential.response.attestationObject)
        }
      };

      // Add transports if available
      if (credential.response.getTransports) {
        credentialJSON.response.transports = credential.response.getTransports();
      }

      console.log('Completing registration...');

      // Complete registration
      await api.post('/webauthn/register/complete', {
        challenge_id: challengeId,
        credential: credentialJSON,
        friendly_name: friendlyName || 'My Passkey'
      });

      toast.success('Passkey registered successfully!');
      setShowRegisterDialog(false);
      setFriendlyName('');
      fetchCredentials();

    } catch (err) {
      console.error('Registration error:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      
      if (err.name === 'NotAllowedError') {
        toast.error('Registration was cancelled or timed out. Please try again.');
      } else if (err.name === 'SecurityError') {
        toast.error('Security error - make sure you are using HTTPS');
      } else if (err.name === 'NotSupportedError') {
        toast.error('Your device does not support passkey registration. Try using a different device.');
      } else if (err.name === 'InvalidStateError') {
        toast.error('A passkey for this account already exists on this device.');
      } else if (err.name === 'AbortError') {
        toast.error('Registration was aborted. Please try again.');
      } else if (err.message?.includes('not supported')) {
        toast.error('Passkeys are not supported on this device/browser. Try using Chrome, Safari, or Edge on a device with biometric capabilities.');
      } else {
        toast.error(err.response?.data?.detail || err.message || 'Failed to register passkey. Please try on a device with biometric support.');
      }
    } finally {
      setRegistering(false);
    }
  };

  // Delete credential
  const handleDelete = async (credentialId) => {
    if (!confirm('Are you sure you want to delete this passkey?')) return;

    try {
      await api.delete(`/webauthn/credentials/${credentialId}`);
      toast.success('Passkey deleted');
      fetchCredentials();
    } catch (err) {
      toast.error('Failed to delete passkey');
    }
  };

  // Update preferred method
  const handlePreferredMethod = async (method) => {
    try {
      await api.post('/webauthn/set-preferred-method', { method });
      setPreferredMethod(method);
      toast.success(`Preferred authentication method set to ${method}`);
    } catch (err) {
      toast.error('Failed to update preference');
    }
  };

  // Toggle email MFA
  const toggleEmailMfa = async () => {
    try {
      if (user?.mfa_enabled) {
        await api.post('/auth/disable-mfa');
        toast.success('Email MFA disabled');
      } else {
        await api.post('/auth/enable-mfa');
        toast.success('Email MFA enabled');
      }
      // Refresh user data would be handled by parent component
      window.location.reload();
    } catch (err) {
      toast.error('Failed to update MFA settings');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center justify-center">
          <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Two-Factor Authentication (2FA)
          </CardTitle>
          <CardDescription>
            Secure your account with additional authentication methods
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email MFA */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-full">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium">Email Code</h4>
                <p className="text-sm text-slate-500">
                  Receive a 6-digit code via email
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {user?.mfa_enabled ? (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Enabled
                </Badge>
              ) : (
                <Badge variant="secondary">Disabled</Badge>
              )}
              <Button
                variant={user?.mfa_enabled ? "outline" : "default"}
                size="sm"
                onClick={toggleEmailMfa}
              >
                {user?.mfa_enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          {/* Biometric / Passkeys */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-full">
                <Fingerprint className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium">Biometric / Face ID</h4>
                <p className="text-sm text-slate-500">
                  Use fingerprint, face recognition, or device PIN
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {credentials.length > 0 ? (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {credentials.length} Passkey{credentials.length > 1 ? 's' : ''}
                </Badge>
              ) : (
                <Badge variant="secondary">Not Set Up</Badge>
              )}
              <Button
                size="sm"
                onClick={() => setShowRegisterDialog(true)}
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Passkey
              </Button>
            </div>
          </div>

          {!webauthnSupported && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-700">
                <p className="font-medium">WebAuthn not supported</p>
                <p>Your browser doesn't support passkeys. Please use a modern browser like Chrome, Safari, Firefox, or Edge.</p>
              </div>
            </div>
          )}

          {/* Preferred Method */}
          {(user?.mfa_enabled || credentials.length > 0) && (
            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Preferred Authentication Method</Label>
              <p className="text-xs text-slate-500 mb-2">
                Choose which method to use by default when logging in
              </p>
              <Select value={preferredMethod} onValueChange={handlePreferredMethod}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {user?.mfa_enabled && (
                    <SelectItem value="email">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email Code
                      </div>
                    </SelectItem>
                  )}
                  {credentials.length > 0 && (
                    <SelectItem value="webauthn">
                      <div className="flex items-center gap-2">
                        <Fingerprint className="w-4 h-4" />
                        Biometric / Face ID
                      </div>
                    </SelectItem>
                  )}
                  <SelectItem value="none">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Password Only
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Registered Passkeys */}
      {credentials.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Registered Passkeys
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {credentials.map((cred) => (
                <div
                  key={cred.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="font-medium">{cred.friendly_name}</p>
                      <p className="text-xs text-slate-500">
                        Added {new Date(cred.created_at).toLocaleDateString()}
                        {cred.last_used && (
                          <> · Last used {new Date(cred.last_used).toLocaleDateString()}</>
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(cred.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Register Passkey Dialog */}
      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fingerprint className="w-5 h-5" />
              Register New Passkey
            </DialogTitle>
            <DialogDescription>
              Add a new biometric or device authentication method
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Passkey Name</Label>
              <Input
                placeholder="e.g., My iPhone, Work Laptop"
                value={friendlyName}
                onChange={(e) => setFriendlyName(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">
                Give your passkey a name to identify it later
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                When you click "Register", your device will prompt you to use:
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• Fingerprint scanner</li>
                <li>• Face ID / Face recognition</li>
                <li>• Device PIN or pattern</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegisterDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleRegister} disabled={registering}>
              {registering ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  Registering...
                </>
              ) : (
                <>
                  <Fingerprint className="w-4 h-4 mr-2" />
                  Register Passkey
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecuritySettings;
